import { Collapse, Empty } from 'antd';
import { groupByDay, relativeDay, formatShortDate, formatTimestamp } from './timelineUtils';

// One row: dot + connector rail, then the body and a single muted meta line
// (author · time · date). Shared by both the grouped (Collapse-of-days) and
// flat rendering below, so a row looks identical either way.
function TimelineRow({ row, last, getTime, getAuthor, getMeta, renderBody }) {
  const time = getTime(row);
  const author = getAuthor?.(row);
  const meta = getMeta?.(row);

  return (
    <div className="znx-an-item">
      <div className="znx-an-rail">
        {meta ? (
          <span
            className={`znx-an-icon-dot znx-an-icon-dot--${meta.tone}`}
            title={meta.label}
          >
            {meta.icon}
          </span>
        ) : (
          <span className="znx-an-dot" />
        )}
        {!last && <span className="znx-an-line" />}
      </div>
      <div className="znx-an-content">
        {renderBody(row)}
        <div className="znx-an-meta">
          {author && (
            <>
              <span className="znx-an-author">{author}</span>
              <span className="znx-an-sep" aria-hidden="true" />
            </>
          )}
          {/* One timestamp, in the tenant's standard format, reading date then
              time — the same as every other timestamp in the app. It used to be
              two spans in the opposite order with a different date spelling. */}
          <span className="znx-an-stamp">{formatTimestamp(time)}</span>
        </div>
      </div>
    </div>
  );
}

/*
 * Renders timeline rows either grouped into a Collapse of calendar days
 * (default) or, with `grouped={false}`, as one flat list — same row markup
 * either way, just without the day-header/collapse chrome. `getTime` reads
 * the row's timestamp, `renderBody` renders the row-specific content (note
 * text / activity html) and the optional `getAuthor` supplies a name for the
 * meta line.
 *
 * Reading order inside a row is message first, then a single muted meta line
 * (author · time · date). The message is what the user came for; the timestamp
 * is a reference they only reach for once the message has landed.
 */
export default function TimelineGroups({
  rows = [],
  getTime,
  getKey,
  getAuthor,
  getMeta,
  renderBody,
  emptyText = 'Nothing here yet.',
  grouped = true,
}) {
  if (!grouped) {
    if (!rows.length) {
      return (
        <div className="znx-an-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
        </div>
      );
    }
    return (
      <div className="znx-an-flat">
        {rows.map((row, rowIndex) => (
          <TimelineRow
            key={getKey(row, rowIndex)}
            row={row}
            last={rowIndex === rows.length - 1}
            getTime={getTime}
            getAuthor={getAuthor}
            getMeta={getMeta}
            renderBody={renderBody}
          />
        ))}
      </div>
    );
  }

  const groups = groupByDay(rows, getTime);

  if (!groups.length) {
    return (
      <div className="znx-an-empty">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
      </div>
    );
  }

  const items = groups.map((group, groupIndex) => ({
    key: String(groupIndex),
    // The full day lives here, once per group, instead of being repeated on
    // every row underneath it.
    label: (
      <span className="znx-an-group">
        <span className="znx-an-group-day">{relativeDay(group.date)}</span>
        <span className="znx-an-group-date">{formatShortDate(group.date)}</span>
        <span className="znx-an-group-count">{group.items.length}</span>
      </span>
    ),
    children: group.items.map((row, rowIndex) => (
      <TimelineRow
        key={getKey(row, rowIndex)}
        row={row}
        last={rowIndex === group.items.length - 1}
        getTime={getTime}
        getAuthor={getAuthor}
        getMeta={getMeta}
        renderBody={renderBody}
      />
    )),
  }));

  return (
    <Collapse
      className="znx-an-collapse"
      defaultActiveKey={['0']}
      ghost
      items={items}
    />
  );
}
